import React from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import useIsMobile from 'hooks/useIsMobile';
import { simpleDate } from 'lib/date';
import { capitalize } from 'lib/util';
import { colors } from 'styles/theme/colors';
import { Label, LabelBold } from 'styles/typography';

export type CoordinateListItemProps = {
  id: string;
  name: string;
  externalId?: string | null;
  dateOfBirth?: Date | null;
  sex?: string | null;
  hospital?: string | null;
  onAddToPortfolio?: (id: string) => void;
};

function CoordinateListItem({
  id,
  name,
  externalId,
  dateOfBirth,
  sex,
  hospital,
  onAddToPortfolio,
}: CoordinateListItemProps) {
  const isMobile = useIsMobile();

  const fields = [
    externalId && { label: 'ID', value: externalId },
    dateOfBirth && { label: 'DOB', value: simpleDate(dateOfBirth) },
    sex && { label: 'Gender', value: capitalize(sex) },
    hospital && { label: 'Hospital', value: hospital },
  ].filter((field): field is { label: string; value: string } => Boolean(field));

  return (
    <Container>
      <Content>
        <InfoSection>
          <NameHeading>{name}</NameHeading>
          <SubTextContainer $isMobile={isMobile}>
            {fields.map((field, index) => (
              <React.Fragment key={field.label}>
                <SubText>
                  <CategoryLabel>{field.label}:</CategoryLabel> {field.value}
                </SubText>
                <DesktopOnly>{index < fields.length - 1 && <BulletPoint>•</BulletPoint>}</DesktopOnly>
              </React.Fragment>
            ))}
            <MobileOnly>
              <StyledButton variant='outline-primary' fullWidth onClick={() => onAddToPortfolio?.(id)}>
                Add to Portfolio
              </StyledButton>
            </MobileOnly>
          </SubTextContainer>
        </InfoSection>
        <DesktopOnly>
          <ButtonSection>
            <StyledButton variant='outline-primary' onClick={() => onAddToPortfolio?.(id)}>
              Add to Portfolio
            </StyledButton>
          </ButtonSection>
        </DesktopOnly>
      </Content>
    </Container>
  );
}

export default React.memo(CoordinateListItem);

const Container = styled.div`
  width: 100%;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.black25};
  border-radius: 4px;
  padding: 24px;
  transition: background-color 0.2s ease;
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const NameHeading = styled(LabelBold)`
  font-size: 16px;
  font-weight: var(--font-weight-bold);
  color: ${colors.black};
`;

const SubTextContainer = styled.div<{ $isMobile: boolean }>`
  display: flex;
  flex-direction: ${(props) => (props.$isMobile ? 'column' : 'row')};
  align-items: ${(props) => (props.$isMobile ? 'flex-start' : 'center')};
  gap: 8px;
  flex-wrap: wrap;
`;

const SubText = styled(Label)`
  font-size: 12px;
  line-height: 16px;
  color: ${colors.black};
`;

const CategoryLabel = styled.span`
  color: ${colors.black50};
`;

const BulletPoint = styled.span`
  font-size: 12px;
  color: ${colors.black50};
`;

const ButtonSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledButton = styled(Button)`
  padding: 8px 24px;
`;
